/**
 * Вкладка «Севооборот» — ввод истории культур по годам (ситуация В-1).
 *
 * Один экран решает одну задачу: агроном садится и вбивает, что росло на поле
 * за последние 10 лет. Строка = год, ячейка = занятие поля. Пустые годы не
 * прячем и не сворачиваем — именно они и есть рабочий список на сегодня.
 *
 * Текущий год здесь только читается: живой посев заводится и ведётся на
 * вкладке «Посевы». Иначе один и тот же сезон правился бы из двух мест.
 */
import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { useFieldStateLabel } from '@/lib/reference/fieldCatalogs';
import { fieldStateLabel } from '../constants';
import { useRotation, usePutRotationYear, type RotationYear, type RotationYearBody } from './api';
import { RotationYearEditor } from './RotationYearEditor';
import { fmtHa, fmtYield, nextEmptyYear, perennialYearLabel } from './rotationMath';

interface Props {
  fieldId: string;
  canWrite: boolean;
  /** Поле рисовой системы — АМП появляется в списке занятий (как в SowForm). */
  isRiceSystem?: boolean;
}

const DEPTH_STEPS = [10, 20, 30];

/** Человеческий текст ошибки сервера (структурированные отказы несут `message`). */
function errText(e: unknown): string {
  if (e instanceof ApiError) {
    const d = (e as ApiError & { detail?: unknown }).detail;
    if (d && typeof d === 'object' && 'message' in d) return String((d as { message: unknown }).message);
    return e.message;
  }
  return e instanceof Error ? e.message : 'Не удалось сохранить год.';
}

export function RotationHistoryTab({ fieldId, canWrite, isRiceSystem = false }: Props) {
  const [depth, setDepth] = useState(10);
  const [onlyEmpty, setOnlyEmpty] = useState(false);
  const [editYear, setEditYear] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fallowLabel = useFieldStateLabel();

  const { data, isLoading, isError } = useRotation(fieldId, depth);
  const put = usePutRotationYear(fieldId);

  const rows = useMemo(() => {
    if (!data) return [];
    return onlyEmpty ? data.years.filter((y) => y.mode === 'empty') : data.years;
  }, [data, onlyEmpty]);

  const filledCount = (data?.years.length ?? 0) - (data?.empty_years.length ?? 0);
  // Кнопка называет год («…— 2022»): он известен, и объяснять дешевле цифрой.
  const nearestEmpty = data?.empty_years.find((y) => y < data.current_year) ?? null;

  function save(year: number, body: RotationYearBody, goTo: number | null) {
    setError(null);
    put.mutate(
      { year, body },
      {
        onSuccess: () => {
          toast.success(`${year} год сохранён`);
          setEditYear(goTo);
        },
        onError: (e) => setError(errText(e)),
      },
    );
  }

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (isError || !data) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Не удалось загрузить севооборот поля.</AlertDescription>
      </Alert>
    );
  }

  // Пусто — нормальное состояние, из которого есть выход, а не сломанный экран.
  const nothingFilled = filledCount === 0 && editYear == null;
  if (nothingFilled) {
    const firstArchive = data.current_year - 1;
    return (
      <Card className="my-3">
        <CardContent className="space-y-3 py-8 text-center">
          <p className="text-base font-semibold">Что росло на этом поле — мы пока не знаем</p>
          <p className="mx-auto max-w-xl text-sm text-muted-foreground">
            Без истории не посчитать предшественника, не спланировать чередование и не опереться
            на урожайность прошлых лет при планировании сезона.
          </p>
          <div className="flex flex-wrap justify-center gap-2 pt-1">
            <Button disabled={!canWrite} onClick={() => setEditYear(firstArchive)}>
              Ввести руками — начать с {firstArchive}
            </Button>
            <Button variant="outline" disabled>
              Забрать историю из АгроХистори
            </Button>
            <Button variant="ghost" disabled>
              Загрузить файлом
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Импорт из АгроХистори и загрузка файлом придут отдельно — то, что импорт не найдёт,
            останется пустыми строками и дозаполняется руками в этой же таблице.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Годы
        </span>
        {DEPTH_STEPS.map((d) => (
          <Button
            key={d}
            size="sm"
            variant={depth === d ? 'default' : 'outline'}
            onClick={() => setDepth(d)}
          >
            {d} лет
          </Button>
        ))}
        <Button
          size="sm"
          variant={onlyEmpty ? 'default' : 'outline'}
          onClick={() => setOnlyEmpty((v) => !v)}
        >
          Только пустые · {data.empty_years.length}
        </Button>
        <Badge variant="secondary" className="ml-auto">
          Урожайность — бункерный вес, ц/га
        </Badge>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="py-2 pr-3 font-medium">Год</th>
              <th className="py-2 pr-3 font-medium">Занятие поля</th>
              <th className="py-2 pr-3 text-right font-medium">Площадь / %</th>
              <th className="py-2 pr-3 text-right font-medium">Урожайность</th>
              <th className="py-2 pr-3 font-medium">Сорт / гибрид</th>
              <th className="py-2 pr-3 font-medium">Источник</th>
              <th className="py-2 font-medium" />
            </tr>
          </thead>
          <tbody>
            {rows.map((y) => (
              <YearRow
                key={y.year}
                year={y}
                fieldAreaHa={data.field_area_ha}
                isRiceSystem={isRiceSystem}
                editing={editYear === y.year}
                canWrite={canWrite}
                saving={put.isPending}
                error={editYear === y.year ? error : null}
                emptyYears={data.empty_years}
                fallowLabel={fallowLabel}
                onEdit={() => {
                  setError(null);
                  setEditYear(y.year);
                }}
                onCancel={() => setEditYear(null)}
                onSave={(body, goTo) => save(y.year, body, goTo)}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
        <span className="text-xs text-muted-foreground">
          Заполнено <b className="text-foreground">{filledCount}</b> из {data.years.length} лет
          {data.empty_years.length > 0 && ` · пустые: ${data.empty_years.join(', ')}`}
        </span>
        {canWrite && nearestEmpty != null && (
          <Button size="sm" onClick={() => setEditYear(nearestEmpty)}>
            Заполнить ближайший пустой — {nearestEmpty}
          </Button>
        )}
      </div>
    </div>
  );
}

interface RowProps {
  year: RotationYear;
  fieldAreaHa: number | null;
  isRiceSystem: boolean;
  editing: boolean;
  canWrite: boolean;
  saving: boolean;
  error: string | null;
  emptyYears: number[];
  fallowLabel: ReturnType<typeof useFieldStateLabel>;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (body: RotationYearBody, goTo: number | null) => void;
}

function YearRow({
  year,
  fieldAreaHa,
  isRiceSystem,
  editing,
  canWrite,
  saving,
  error,
  emptyYears,
  fallowLabel,
  onEdit,
  onCancel,
  onSave,
}: RowProps) {
  if (editing) {
    return (
      <tr>
        <td colSpan={7} className="py-3">
          <RotationYearEditor
            year={year.year}
            fieldAreaHa={fieldAreaHa}
            isRiceSystem={isRiceSystem}
            existing={year}
            nextEmpty={nextEmptyYear(emptyYears, year.year)}
            saving={saving}
            error={error}
            onCancel={onCancel}
            onSave={onSave}
          />
        </td>
      </tr>
    );
  }

  const empty = year.mode === 'empty';
  return (
    <tr className={cn('border-b border-border/60 align-middle', empty && 'text-muted-foreground')}>
      <td className="py-3 pr-3 font-semibold tabular-nums">
        {year.year}
        {year.is_current && (
          <span className="block text-xs font-medium uppercase text-muted-foreground">
            текущий
          </span>
        )}
      </td>
      {empty ? (
        <td colSpan={5} className="py-3 pr-3 text-sm">
          не заполнено — что росло, неизвестно
        </td>
      ) : (
        <>
          <td className="py-3 pr-3">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              {year.entries.map((e, i) => (
                <span key={e.sowing_id + String(i)} className="flex items-center gap-2">
                  {i > 0 && (
                    <span className="text-muted-foreground">
                      {year.mode === 'sequential' ? '→' : '+'}
                    </span>
                  )}
                  <span className="font-medium">
                    {e.kind === 'fallow'
                      ? fieldStateLabel(e.fallow_kind, fallowLabel)
                      : (e.crop_name ?? 'Культура')}
                  </span>
                  {perennialYearLabel(e.perennial_year) && (
                    <Badge variant="secondary" className="px-2 py-0 text-xs">
                      {perennialYearLabel(e.perennial_year)}
                    </Badge>
                  )}
                </span>
              ))}
            </div>
            {year.predecessor_label && (
              <p className="mt-1 text-xs text-muted-foreground">
                {year.mode === 'sequential' && 'одна за другой · '}
                {year.mode === 'parallel' && 'одновременно · '}
                предшественник для {year.year + 1} — {year.predecessor_label}
              </p>
            )}
          </td>
          <td className="py-3 pr-3 text-right tabular-nums">
            {fmtHa(year.occupied_ha)}
            {fieldAreaHa && year.occupied_ha != null && (
              <span className="block text-xs text-muted-foreground">
                {Math.round((year.occupied_ha / fieldAreaHa) * 100)} %
              </span>
            )}
          </td>
          <td className="py-3 pr-3 text-right tabular-nums">
            {year.entries.every((e) => e.kind === 'fallow')
              ? <span className="text-xs text-muted-foreground">не применимо</span>
              : year.entries.map((e) => fmtYield(e.yield_c_ha)).join(' / ')}
          </td>
          <td className="py-3 pr-3 text-xs text-muted-foreground">
            {year.entries.flatMap((e) => e.variety_names).join(', ') || 'не заводили'}
          </td>
          <td className="py-3 pr-3">
            <Badge variant={year.entries[0]?.source === 'import' ? 'outline' : 'secondary'}>
              {year.entries[0]?.source === 'import' ? 'импорт' : 'введено у нас'}
            </Badge>
          </td>
        </>
      )}
      <td className="py-3 text-right">
        {year.editable && canWrite && (
          <Button size="sm" variant={empty ? 'outline' : 'ghost'} onClick={onEdit}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {empty ? 'Заполнить' : 'Править'}
          </Button>
        )}
        {!year.editable && (
          <span className="text-xs text-muted-foreground">ведётся на «Посевах»</span>
        )}
      </td>
    </tr>
  );
}
